import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import productsData from '../../data/products';
import './productdetails.css';
import ProductPictures from './productpictures/ProductPictures';
import ProductHighlights from './producthighlights/ProductHighlights';
import ProductSpecifications from './productspecifications/ProductSpecifications';
import YouTubeEmbed from './productyoutube/ProductYoutube';
import ProductModels from './productmodels/ProductModels';
import ProductFaq from './productfaq/ProductFaq';
import NotFound from '../../pages/notfound/NotFound';

const SITE_URL = 'https://cyclesolutions.se';

const CATEGORY_PAGES = {
    Balpressar: { name: 'Balpressar', path: '/produkter/balpressar' },
    Komprimatorer: { name: 'Komprimatorer', path: '/produkter/komprimatorer' },
    'Övrigt': { name: 'Övrigt', path: '/produkter/ovrigt' },
};

const ProductDetails = () => {
    const { productId } = useParams();
    // Looked up synchronously so the product is part of the first render (and of the prerendered HTML).
    const product = productsData.find(p => p.id === productId);

    if (!product) {
        return <NotFound />;
    }

    const canonicalUrl = `${SITE_URL}/produkter/${product.id}`;
    const imageUrl = product.imageUrl
        ? `${SITE_URL}${product.imageUrl}`
        : null;

    const hasYoutube = !!product.videoId;
    const isCompOrCC = product.typ === 'Komprimatorer' || product.serie === 'Horisontella balpressar';

    const category = CATEGORY_PAGES[product.typ];
    const breadcrumbs = [
        { name: 'Hem', url: `${SITE_URL}/` },
        { name: 'Produkter', url: `${SITE_URL}/produkter` },
        ...(category ? [{ name: category.name, url: `${SITE_URL}${category.path}` }] : []),
        { name: product.namn, url: canonicalUrl },
    ];
    const breadcrumbJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.url,
        })),
    };

    return (
        <div className="product___details">
            <Helmet>
                <title>{product.seoTitle}</title>
                <meta name="description" content={product.seoDescription} />
                <link rel="canonical" href={canonicalUrl} />

                {/* Open Graph */}
                <meta property="og:title" content={product.seoTitle} />
                <meta property="og:description" content={product.seoDescription} />
                {imageUrl && <meta property="og:image" content={imageUrl} />}
                <meta property="og:type" content="product" />
                <meta property="og:url" content={canonicalUrl} />

                <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
            </Helmet>

            <ProductPictures product={product} />
            <ProductHighlights product={product} />
            {hasYoutube && <YouTubeEmbed product={product} />}
            {!isCompOrCC && <ProductSpecifications product={product} />}
            {product.modeller && <ProductModels product={product} />}
            <ProductFaq product={product} />
        </div>
    );
};

export default ProductDetails;
